import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Shield, Camera, MapPin, Mic, Brain, Bell, AlertTriangle } from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher, poster } from '../api/client'

interface AgentSettings {
  id: number
  elderly_id: number
  health_monitoring_enabled: boolean
  medication_reminders_enabled: boolean
  emotion_analysis_level: number
  camera_enabled: boolean
  fall_detection_enabled: boolean
  location_tracking_enabled: boolean
  voice_clone_enabled: boolean
  memory_prompts_enabled: boolean
  daily_checkin_enabled: boolean
  notify_supervisor_on_alert: boolean
  language: string
  agent_name: string
}

export default function SettingsPanel() {
  const { t, i18n } = useTranslation()
  const { selectedElderlyId, setLanguage } = useAgentStore()
  const queryClient = useQueryClient()
  const [showSensitiveConfirm, setShowSensitiveConfirm] = useState<string | null>(null)

  const { data: settings, isLoading } = useQuery<AgentSettings>({
    queryKey: ['settings', selectedElderlyId],
    queryFn: () => fetcher(`/settings/${selectedElderlyId}`),
    enabled: !!selectedElderlyId,
  })

  const mutation = useMutation({
    mutationFn: (data: Partial<AgentSettings>) => 
      poster(`/settings/${selectedElderlyId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', selectedElderlyId] })
    },
    onError: (error: any) => {
      console.error('Settings update failed', error);
      alert(error?.response?.data?.detail || 'Failed to update settings');
    },
  });

  const handleToggle = (field: keyof AgentSettings, value: boolean) => {
    // Level 3 permission check for sensitive features
    const sensitiveFields = ['camera_enabled', 'voice_clone_enabled', 'fall_detection_enabled']
    if (sensitiveFields.includes(field) && value) {
      setShowSensitiveConfirm(field)
      return
    }
    mutation.mutate({ [field]: value })
  }

  const confirmSensitive = (field: string) => {
    mutation.mutate({ [field]: true })
    setShowSensitiveConfirm(null)
  }

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
    setLanguage(lang)
    mutation.mutate({ language: lang })
  }

  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('settings.no_elderly_selected')}
      </div>
    )
  }

  if (isLoading) {
    return <div className="text-center py-20 text-xl">{t('common.loading')}</div>
  }

  const sections = [
    {
      title: t('settings.health'),
      icon: Shield,
      items: [
        { key: 'health_monitoring_enabled', label: t('settings.health_monitoring'), level: 0 },
        { key: 'medication_reminders_enabled', label: t('settings.medication_reminders'), level: 1 },
        { key: 'anomaly_detection_enabled', label: t('settings.anomaly_detection'), level: 1 },
      ],
    },
    {
      title: t('settings.emotion'),
      icon: Brain,
      items: [
        { key: 'emotion_analysis_level', label: t('settings.emotion_analysis'), level: 1, isSelect: true },
        { key: 'depression_screening_enabled', label: t('settings.depression_screening'), level: 1 },
      ],
    },
    {
      title: t('settings.vision'),
      icon: Camera,
      items: [
        { key: 'camera_enabled', label: t('settings.camera'), level: 3 },
        { key: 'fall_detection_enabled', label: t('settings.fall_detection'), level: 2 },
        { key: 'skin_analysis_enabled', label: t('settings.skin_analysis'), level: 2 },
      ],
    },
    {
      title: t('settings.location'),
      icon: MapPin,
      items: [
        { key: 'location_tracking_enabled', label: t('settings.location_tracking'), level: 2 },
      ],
    },
    {
      title: t('settings.voice'),
      icon: Mic,
      items: [
        { key: 'tts_enabled', label: t('settings.tts'), level: 1 },
        { key: 'voice_clone_enabled', label: t('settings.voice_clone'), level: 3 },
      ],
    },
    {
      title: t('settings.companion'),
      icon: Bell,
      items: [
        { key: 'memory_prompts_enabled', label: t('settings.memory_prompts'), level: 1 },
        { key: 'daily_checkin_enabled', label: t('settings.daily_checkin'), level: 1 },
        { key: 'notify_supervisor_on_alert', label: t('settings.notify_supervisor'), level: 1 },
      ],
    },
  ]

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('settings.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Language Selector */}
      <div className="card">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          {t('settings.language')}
        </h3>
        <div className="flex gap-4">
          {[
            { code: 'en', label: 'English' },
            { code: 'ar', label: 'العربية' },
            { code: 'fr', label: 'Français' },
            { code: 'es', label: 'Español' },
          ].map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`px-6 py-3 rounded-xl text-lg font-medium transition-colors ${
                i18n.language === lang.code
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      {/* Settings Sections */}
      {sections.map((section) => (
        <div key={section.title} className="card">
          <div className="flex items-center gap-3 mb-6">
            <section.icon size={28} className="text-primary-600" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{section.title}</h3>
          </div>

          <div className="space-y-4">
            {section.items.map((item: any) => (
              <div
                key={item.key}
                className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg text-gray-800 dark:text-gray-200">{item.label}</span>
                  {item.level === 3 && (
                    <span className="px-2 py-1 bg-danger-50 text-danger-600 text-xs font-bold rounded-lg">
                      {t('permissions.sensitive')}
                    </span>
                  )}
                </div>

                {item.isSelect ? (
                  <select
                    className="input-field w-48"
                    value={Number(settings?.[item.key as keyof AgentSettings] ?? 1)}
                    onChange={(e) => mutation.mutate({ [item.key]: parseInt(e.target.value) })}
                  >
                    <option value={0}>{t('emotion.disabled')}</option>
                    <option value={1}>{t('emotion.text_only')}</option>
                    <option value={2}>{t('emotion.text_voice')}</option>
                    <option value={3}>{t('emotion.full')}</option>
                  </select>
                ) : (
                  <button
                    onClick={() =>
                      handleToggle(
                        item.key as keyof AgentSettings,
                        !settings?.[item.key as keyof AgentSettings]
                      )
                    }
                    className={`relative w-16 h-9 rounded-full transition-colors ${
                      settings?.[item.key as keyof AgentSettings]
                        ? 'bg-primary-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow transition-transform ${
                        settings?.[item.key as keyof AgentSettings]
                          ? 'translate-x-8'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Sensitive Feature Confirmation Modal */}
      {showSensitiveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 text-danger-600 mb-4">
              <AlertTriangle size={32} />
              <h3 className="text-2xl font-bold">{t('settings.sensitive_confirm_title')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
              {t('settings.sensitive_confirm_message')}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowSensitiveConfirm(null)}
                className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => confirmSensitive(showSensitiveConfirm)}
                className="flex-1 py-3 rounded-xl bg-danger-600 text-white font-semibold hover:bg-danger-700"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}